import React from "react";
import { Search, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { fetchImportBatches, fetchSupplierTypes, fetchSuppliers } from "@/lib/api/manufacturing";

function StatusBadge({ active }) {
  return (
    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
      {active ? "Activo" : "Inactivo"}
    </span>
  );
}

function BatchStatus({ status }) {
  const className =
    status === "completed"
      ? "bg-green-100 text-green-700"
      : status === "failed"
        ? "bg-red-100 text-red-700"
        : "bg-yellow-100 text-yellow-800";
  return <span className={`rounded-full px-2 py-1 text-xs font-semibold ${className}`}>{status || "running"}</span>;
}

export function SupplierReadOnly({ mode = "suppliers" }) {
  const [suppliers, setSuppliers] = React.useState([]);
  const [supplierTypes, setSupplierTypes] = React.useState([]);
  const [batches, setBatches] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const { toast } = useToast();

  const loadData = React.useCallback(async () => {
    setLoading(true);
    const [supplierResult, typeResult, batchResult] = await Promise.all([
      fetchSuppliers(searchTerm.trim()),
      fetchSupplierTypes(),
      fetchImportBatches(),
    ]);

    if (supplierResult.error || typeResult.error || batchResult.error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los catálogos ERP. Revisa RLS o migraciones.",
        variant: "destructive",
      });
    }

    setSuppliers(supplierResult.data || []);
    setSupplierTypes(typeResult.data || []);
    setBatches(batchResult.data || []);
    setLoading(false);
  }, [searchTerm, toast]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const typeSearch = searchTerm.trim().toLowerCase();
  const filteredSupplierTypes = typeSearch
    ? supplierTypes.filter((type) =>
        [type.erp_code, type.description].some((value) => String(value || "").toLowerCase().includes(typeSearch))
      )
    : supplierTypes;
  const isSupplierTypesMode = mode === "supplier-types";

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">{isSupplierTypesMode ? "Tipos de proveedor ERP" : "Proveedores ERP"}</h2>
            <p className="text-sm text-gray-600">
              Catálogo de solo lectura importado desde el ERP. Las altas y cambios vendrán del proceso de importación.
            </p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refrescar
          </Button>
        </div>

        <div className="mt-4 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={isSupplierTypesMode ? "Buscar por código o descripción" : "Buscar por código, nombre o ciudad"}
              className="pl-9"
            />
          </div>
          <Button onClick={loadData}>Buscar</Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="text-2xl font-bold">{suppliers.length}</div>
          <div className="text-sm text-gray-600">Proveedores visibles</div>
        </div>
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="text-2xl font-bold">{supplierTypes.length}</div>
          <div className="text-sm text-gray-600">Tipos proveedor</div>
        </div>
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="text-2xl font-bold">{batches.length}</div>
          <div className="text-sm text-gray-600">Imports registrados</div>
        </div>
      </div>

      {isSupplierTypesMode ? (
        <div className="rounded-lg border bg-white shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código ERP</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Actualizado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3}>Cargando tipos de proveedor...</TableCell>
                </TableRow>
              ) : filteredSupplierTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>No hay tipos de proveedor importados.</TableCell>
                </TableRow>
              ) : (
                filteredSupplierTypes.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell className="font-mono">{type.erp_code}</TableCell>
                    <TableCell className="font-medium">{type.description}</TableCell>
                    <TableCell>{type.updated_at ? new Date(type.updated_at).toLocaleDateString("es-ES") : "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="rounded-lg border bg-white shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código ERP</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Ciudad</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6}>Cargando proveedores...</TableCell>
                </TableRow>
              ) : suppliers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>No hay proveedores importados.</TableCell>
                </TableRow>
              ) : (
                suppliers.map((supplier) => (
                  <TableRow key={supplier.id}>
                    <TableCell className="font-mono">{supplier.erp_code}</TableCell>
                    <TableCell className="font-medium">{supplier.name}</TableCell>
                    <TableCell>{supplier.supplier_type?.description || supplier.legacy_type_code || "-"}</TableCell>
                    <TableCell>{[supplier.city, supplier.province].filter(Boolean).join(", ") || "-"}</TableCell>
                    <TableCell>{supplier.email || supplier.phone || "-"}</TableCell>
                    <TableCell>
                      <StatusBadge active={supplier.is_active} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

